import forEach from 'lodash/forEach';
import get from 'lodash/get';
import { getExperiment } from '../../../../../js/utils/getExperiment';
import logger from '../../../../logging/logger';
import { IMA_VIDEO_PLAYER_ID } from '../../../constants';
import Events from '../../Events';
import ImaApi from './ImaApi';
import imaEvents from './imaEvents';
const COMPANION_AD_SIZES = [
    {
        width: 300,
        height: 600,
    },
    {
        width: 300,
        height: 250,
    },
    {
        width: 728,
        height: 90,
    },
];
export default class ImaPlayer extends ImaApi {
    constructor(google) {
        super(google);
        this.adContainer = null;
        this.adsManager = null;
        this.imaVideoElement = null;
        this.playerContainerElement = null;
        this.ad = {};
        this.started = false;
        this.onAdLoaded = this.onAdLoaded.bind(this);
        this.onAdError = this.onAdError.bind(this);
        this.onAdStarted = this.onAdStarted.bind(this);
        this.onAdSkipped = this.onAdSkipped.bind(this);
        this.onAdImpression = this.onAdImpression.bind(this);
        this.getCompanionAds = this.getCompanionAds.bind(this);
        this.onAdProgress = this.onAdProgress.bind(this);
        this.onAdPaused = this.onAdPaused.bind(this);
        this.onAdResumed = this.onAdResumed.bind(this);
        this.onAdComplete = this.onAdComplete.bind(this);
        this.onAllAdsCompleted = this.onAllAdsCompleted.bind(this);
        this.onVideoAdStartTimeout = this.onVideoAdStartTimeout.bind(this);
    }
    initialize(videoAdContainerId, settings = {}) {
        this.imaVideoElement = global.document.getElementById(IMA_VIDEO_PLAYER_ID);
        this.playerContainerElement =
            global.document.getElementById(videoAdContainerId);
        this.validateAndSetVolume(settings.volume);
        if (this.imaVideoElement && this.playerContainerElement) {
            return;
        }
        this.imaVideoElement = global.document.createElement('video');
        this.imaVideoElement.setAttribute('id', IMA_VIDEO_PLAYER_ID);
        if (settings.isMobile) {
            this.imaVideoElement.setAttribute('playsinline', '');
            this.playBlankMp3(settings.blankMp3Url);
        }
        this.playerContainerElement.appendChild(this.imaVideoElement);
    }
    playBlankMp3(blankMp3Url) {
        const onCanPlay = () => {
            this.imaVideoElement.removeEventListener('canplay', onCanPlay);
            this.imaVideoElement.play();
        };
        this.imaVideoElement.addEventListener('canplay', onCanPlay);
        this.imaVideoElement.src = blankMp3Url;
        this.imaVideoElement.load();
    }
    preload() {
        try {
            if (!this.google?.ima) {
                this.google = global.google;
            }
            this.adContainer = this.getAdContainer(this.playerContainerElement, this.imaVideoElement);
        }
        catch (ex) {
            this.adContainer = null;
        }
    }
    async _load(url) {
        if (!(this.google?.ima && this.adContainer)) {
            throw new Error('Google IMA SDK not initialized');
        }
        this.adsManager = await this.getAdsManager(url, this.adContainer, this.imaVideoElement);
        this.adsManager.init(640, 480, this.google.ima.ViewMode.NORMAL);
    }
    async start(url) {
        try {
            await this._load(url);
            this.adsCount = 0;
            const ima = get(this, 'google.ima');
            this.adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, this.onAdError);
            this.adsManager.addEventListener(ima.AdEvent.Type.LOADED, this.onAdLoaded);
            this.adsManager.addEventListener(ima.AdEvent.Type.STARTED, this.onAdStarted);
            this.adsManager.addEventListener(ima.AdEvent.Type.STARTED, this.getCompanionAds);
            this.adsManager.addEventListener(ima.AdEvent.Type.AD_PROGRESS, this.onAdProgress);
            this.adsManager.addEventListener(ima.AdEvent.Type.IMPRESSION, this.onAdImpression);
            this.adsManager.addEventListener(ima.AdEvent.Type.PAUSED, this.onAdPaused);
            this.adsManager.addEventListener(ima.AdEvent.Type.RESUMED, this.onAdResumed);
            this.adsManager.addEventListener(ima.AdEvent.Type.COMPLETE, this.onAdComplete);
            this.adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, this.onAllAdsCompleted);
            this.adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, this.onAdSkipped);
            this.adsManager.setVolume(this.imaPlayerVolume);
            this.adsManager.start();
        }
        catch (ex) {
            this.onAdError(ex);
        }
    }
    resume() {
        if (!this.adsManager) {
            return;
        }
        this.adsManager.resume();
    }
    pause() {
        if (!this.adsManager) {
            return;
        }
        this.adsManager.pause();
    }
    stop() {
        if (!this.adsManager) {
            return;
        }
        this.adsManager.stop();
    }
    resize(width, height) {
        if (!this.adsManager) {
            return;
        }
        this.adsManager.resize(width, height, this.google.ima.ViewMode.NORMAL);
    }
    volume(volume) {
        this.validateAndSetVolume(volume);
        if (!this.adsManager) {
            return;
        }
        this.adsManager.setVolume(this.imaPlayerVolume);
    }
    validateAndSetVolume(volume) {
        this.imaPlayerVolume = volume <= 1 ? volume : volume / 100;
    }
    unload() {
        this.stop();
        this.ad = {};
        if (this.adContainer) {
            this.adContainer.destroy();
            this.adContainer = null;
        }
        if (this.adsManager) {
            this.adsManager.destroy();
            this.adsManager = null;
        }
        this.playerContainerElement.querySelectorAll('div').forEach((el) => {
            el.remove();
        });
    }
    destroy() {
        this.unload();
        this.imaVideoElement = null;
        this.playerContainerElement = null;
        this.emit(imaEvents.AdEvent.ALL_ADS_COMPLETED);
    }
    onAdError(ex) {
        const imaErrorCodes = get(this, 'google.ima.AdError.ErrorCode', {});
        const error = ex?.getError?.();
        const innerError = error?.getInnerError?.();
        const innerErrorCode = innerError?.getErrorCode?.();
        const errorType = error?.getType?.();
        if (innerErrorCode === imaErrorCodes.AUTOPLAY_DISALLOWED) {
            this.emit(imaEvents.AdEvent.AUTOPLAY_DISALLOWED_ERROR, innerError);
        }
        this.emit(imaEvents.AdEvent.AD_ERROR, {
            error: error || ex,
            errorType,
            adInfo: this.ad,
        });
    }
    onVideoAdStartTimeout() {
        if (this.started) {
            return;
        }
        this.emit(imaEvents.VideoEvent.AD_COMPLETED, this.ad);
        this.emit(imaEvents.AdEvent.AD_ERROR, {
            error: new Error('Ad Playback Start Timeout Error'),
            errorType: Events.ErrorTypes.AD_PLAY_START_TIMEOUT,
            adInfo: this.ad,
        });
    }
    onAdLoaded(adEvent) {
        try {
            const ad = adEvent.getAd();
            const adPodInfo = ad.getAdPodInfo();
            const adData = adEvent.getAdData();
            const contentType = ad.getContentType();
            this.ad = {
                isVideoAd: contentType?.includes('video'),
                adId: get(adData, 'adId'),
                creativeId: get(adData, 'creativeId'),
                height: ad.getHeight(),
                width: ad.getWidth(),
                bitrate: ad.getVastMediaBitrate(),
                currentAdPosition: adPodInfo.getAdPosition(),
                totalAdsInPod: adPodInfo.getTotalAds(),
                clickThroughUrl: adEvent.getAdData().clickThroughUrl,
            };
            if (!this.ad.isVideoAd) {
                this.emit(imaEvents.AudioEvent.AD_LOADED, this.ad);
                this.emit(imaEvents.AdEvent.AD_LOADED, this.ad);
                return;
            }
            this.emit(imaEvents.VideoEvent.AD_LOADED, this.ad);
            this.emit(imaEvents.AdEvent.AD_LOADED, this.ad);
            setTimeout(this.onVideoAdStartTimeout, getExperiment('ads.video.preroll.playbackStarted.timeoutMs', 2000));
        }
        catch (ex) {
            this.onAdError(ex);
        }
    }
    onAdSkipped(adEvent) {
        const ad = adEvent.getAd();
        const podInfo = ad.getAdPodInfo();
        const data = {
            ...this.ad,
            adPosition: podInfo.getAdPosition(),
        };
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_SKIPPED, data);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_SKIPPED, data);
        }
        this.emit(imaEvents.AdEvent.AD_SKIPPED, data);
    }
    onAdStarted() {
        this.started = true;
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_STARTED, this.ad);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_STARTED, this.ad);
        }
        this.emit(imaEvents.AdEvent.AD_STARTED, this.ad);
    }
    getCompanionAds(adEvent) {
        try {
            const ad = adEvent.getAd();
            const companionAds = this._getCompanionAdsBySize(ad);
            const companions = companionAds.map((companionAd) => ({
                height: companionAd.getHeight(),
                width: companionAd.getWidth(),
                content: companionAd.getContent(),
            }));
            if (this.ad.isVideoAd) {
                this.emit(imaEvents.VideoEvent.AD_COMPANIONS_LOADED, companions);
            }
            else {
                this.emit(imaEvents.AudioEvent.AD_COMPANIONS_LOADED, companions);
            }
            this.emit(imaEvents.AdEvent.AD_COMPANIONS_LOADED, companions);
        }
        catch (ex) {
            this.emit(imaEvents.AdEvent.AD_COMPANIONS_LOAD_ERROR, ex);
        }
    }
    onAdImpression() {
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_IMPRESSION, this.ad);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_IMPRESSION, this.ad);
        }
        this.emit(imaEvents.AdEvent.AD_IMPRESSION, this.ad);
    }
    onAdProgress(adEvent) {
        try {
            const ad = adEvent.getAdData();
            const remainingTime = this.adsManager.getRemainingTime();
            const duration = ad.duration;
            const position = (duration || 0) - (remainingTime || 0);
            if (this.ad.isVideoAd) {
                this.emit(imaEvents.VideoEvent.AD_PROGRESS, { duration, position });
            }
            else {
                this.emit(imaEvents.AudioEvent.AD_PROGRESS, { duration, position });
            }
            this.emit(imaEvents.AdEvent.AD_PROGRESS, { duration, position });
        }
        catch (ex) {
        }
    }
    onAdPaused() {
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_PAUSED, this.ad);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_PAUSED, this.ad);
        }
        this.emit(imaEvents.AdEvent.AD_PAUSED, this.ad);
    }
    onAdResumed() {
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_RESUMED, this.ad);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_RESUMED, this.ad);
        }
        this.emit(imaEvents.AdEvent.AD_RESUMED, this.ad);
    }
    onAllAdsCompleted() {
        if (!this.started) {
            this.emit(imaEvents.AdEvent.AD_ERROR, {
                error: new Error('Ad Playback Error'),
                errorType: Events.ErrorTypes.AD_NO_FILL,
                adInfo: this.ad,
            });
        }
        this.started = false;
        this.imaVideoElement.src = '';
        this.emit(imaEvents.AdEvent.ALL_ADS_COMPLETED, this.ad);
    }
    onAdComplete() {
        if (this.ad.isVideoAd) {
            this.emit(imaEvents.VideoEvent.AD_COMPLETED, this.ad);
        }
        else {
            this.emit(imaEvents.AudioEvent.AD_COMPLETED, this.ad);
        }
        this.emit(imaEvents.AdEvent.AD_COMPLETED, this.ad);
        logger.debug('Ad Completed.');
    }
    _getCompanionAdsBySize(ad) {
        const { ima } = this.google;
        const selectionCriteria = new ima.CompanionAdSelectionSettings();
        selectionCriteria.resourceType =
            ima.CompanionAdSelectionSettings.ResourceType.ALL;
        selectionCriteria.creativeType =
            ima.CompanionAdSelectionSettings.CreativeType.ALL;
        selectionCriteria.sizeCriteria =
            ima.CompanionAdSelectionSettings.SizeCriteria.SELECT_EXACT_MATCH;
        let companionAds = [];
        forEach(COMPANION_AD_SIZES, (dimensions) => {
            const { width, height } = dimensions;
            const companions = ad.getCompanionAds(width, height, selectionCriteria);
            if (!companions) {
                return;
            }
            companionAds = companionAds.concat(companions);
        });
        return companionAds;
    }
}
